import React, { useEffect, useRef } from 'react'
import { getLocale } from '@toasttab/buffet-pui-locale-utilities'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import config from 'config'
import { t } from 'src/translations'
import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'

export type AddressProps = {
  name: string
  address1: string
  address2?: string | null
  city: string
  state: string
  zipcode: string
}

export type LocationMapProps = AddressProps & {
  alwaysShowMap: boolean
  wrapperClassName?: string
  trackerContext?: string
}

export const LocationMap: React.FC<LocationMapProps> = (props) => {
  const { restaurant } = useRestaurant()
  const tracker = useTracker()
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const secondaryAddress = props.address2 ? `, ${props.address2}` : ''
  const addressParams = new URLSearchParams({
    address: `${props.name}, ${props.address1}${secondaryAddress}, ${props.city}, ${props.state} ${props.zipcode}`
  })
  const addressQuery = addressParams.toString()
  const showMap =
    props.alwaysShowMap || restaurant?.content?.footerConfig?.hideMap === false

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handleWindowBlur = () => {
      // Check if the iframe is the active element (user clicked into it)
      if (document.activeElement === iframeRef.current) {
        tracker.track(TrackingEvents.EMBEDDED_GOOGLE_MAPS_CLICKED, {
          context: props.trackerContext,
          location_name: props.name
        })
      }
    }

    window.addEventListener('blur', handleWindowBlur)
    return () => {
      window.removeEventListener('blur', handleWindowBlur)
    }
  }, [tracker, props.name, props.trackerContext])

  return showMap ? (
    <div
      className={props.wrapperClassName ?? 'mapFrame'}
      data-testid='location-map-wrapper'
    >
      <iframe
        ref={iframeRef}
        title={t('location-map.map-of', { name: props.name })}
        width='100%'
        height='100%'
        loading='lazy'
        allowFullScreen
        src={`${
          config.resources.mapEndpoint
        }/map?${addressQuery}&language=${getLocale()}`}
      ></iframe>
    </div>
  ) : null
}

export default LocationMap
